import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import styles from '../Playlist.scss';
import { Drawer, Wizard } from '../../Modules';
import { Icon } from 'semantic-ui-react';
import AddRecommendations from './AddRecommendations';
import circleCloseIcon from '../../../assets/circle_close_icon.svg';
import Thumbnail from './Thumbnail';
import UserPlaylist from '../../App/UserPlaylistQuery';
import { RootConsumer } from '../../RootComponent';
import RecommendationContainer from './RecommendationContainer';

export default class PlaylistRecommendations extends React.Component {
  static propTypes = {
    addToPlaylist: PropTypes.func.isRequired,
    addPlaylistRecommendations: PropTypes.func.isRequired,
    removePlaylistRecommendation: PropTypes.func.isRequired,
    playlistRecommendations: PropTypes.array.isRequired,
    playlist: PropTypes.object.isRequired,
    editable: PropTypes.bool,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object
  };

  constructor(props) {
    super(props);
    this.state = {
      activePlaylist: null,
      addRecommendationsOpen: false,
      scrolledToEnd: false
    };
    this.getAllPlaylists = this.getAllPlaylists.bind(this);
    this.isSelected = this.isSelected.bind(this);
    this.hasOverflow = this.hasOverflow.bind(this);
    this.scrollX = this.scrollX.bind(this);
    this.viewPlaylist = this.viewPlaylist.bind(this);
    this.togglePlaylist = this.togglePlaylist.bind(this);
    this.updateScrollPosition = this.updateScrollPosition.bind(this);
  }

  componentDidMount() {
    // Force re-render after DOM has loaded recommendations carousel
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({ scrolledToBeginning: true });
    this.recommendationsRef.current.addEventListener(
      'scroll',
      this.updateScrollPosition
    );
  }

  componentWillUnmount() {
    this.recommendationsRef.current.removeEventListener(
      'scroll',
      this.updateScrollPosition
    );
  }

  getAllPlaylists() {
    return this.props.viewer.public_playlists.edges.concat(
      this.props.viewer.playlists.edges
    );
  }

  recommendationsRef = createRef();

  isSelected(playlistId) {
    return this.props.playlistRecommendations
      .map(({ node: r }) => r.id)
      .includes(playlistId);
  }

  hasOverflow(numItems) {
    const { current } = this.recommendationsRef;
    const itemWidth = 174;
    if (current && numItems * itemWidth > current.offsetWidth) {
      return true;
    }
    return false;
  }

  scrollX(distance) {
    const { current } = this.recommendationsRef;
    current.scrollTo({
      left: current.scrollLeft + distance,
      top: 0,
      behavior: 'smooth'
    });
    this.updateScrollPosition();
  }

  togglePlaylist(e, data) {
    const id = data.value;
    if (this.isSelected(id)) {
      this.props.removePlaylistRecommendation(id);
    } else {
      this.props.addPlaylistRecommendations([id]);
    }
  }

  viewPlaylist(e, id) {
    this.setState({ activePlaylist: id });
  }

  updateScrollPosition() {
    const { current } = this.recommendationsRef;
    this.setState({
      scrolledToBeginning: current.scrollLeft === 0,
      scrolledToEnd:
        current.scrollLeft + this.recommendationsRef.current.offsetWidth ===
        this.recommendationsRef.current.scrollWidth
    });
  }

  render() {
    const {
      addToPlaylist,
      playlistRecommendations,
      removePlaylistRecommendation,
      editable,
      relay,
      viewer
    } = this.props;
    const {
      activePlaylist,
      addRecommendationsOpen,
      scrolledToBeginning,
      scrolledToEnd
    } = this.state;
    const AddRecommendationsWizard =
      editable &&
        <div>
          <Drawer
            visible={addRecommendationsOpen}
            onHide={() => {}}
          >
            <AddRecommendations
              activePlaylist={activePlaylist}
              playlistRecommendations={playlistRecommendations}
              viewer={viewer}
              relay={relay}
              parentID={this.props.playlist.id}
              togglePlaylist={this.togglePlaylist}
              viewPlaylist={this.viewPlaylist}
              onClose={() =>
                this.setState({
                  addRecommendationsOpen: false,
                  activePlaylist: null
                })}
            />
          </Drawer>
        </div>;
    return (
      <RootConsumer>
        {({ isMobile }) =>
          <div style={{ marginTop: '3em', position: 'relative' }}>
            <p className={styles.playlistLabel}>
              Recommended Music
              {this.hasOverflow(playlistRecommendations.length) &&
                <span style={{ position: 'absolute', right: 0, top: 0 }}>
                  <Icon
                    name='chevron left'
                    style={{
                      cursor: !scrolledToBeginning ? 'pointer' : null,
                      color: scrolledToBeginning ? '#DEDEDF' : null,
                      transition: 'color 0.2s ease-in-out'
                    }}
                    onClick={this.scrollX.bind(null, -300)}
                  />
                  <Icon
                    name='chevron right'
                    style={{
                      cursor: !scrolledToEnd ? 'pointer' : null,
                      marginLeft: '0.5em',
                      color: scrolledToEnd ? '#DEDEDF' : null,
                      transition: 'color 0.2s ease-in-out'
                    }}
                    onClick={this.scrollX.bind(null, 300)}
                  />
                </span>}
            </p>
            <div
              className={styles.recommendations}
              ref={this.recommendationsRef}
            >
              {playlistRecommendations.map(({ node: playlist }) =>
                <Wizard
                  closeIcon
                  key={playlist.id}
                  steps={[
                    {
                      component: UserPlaylist,
                      componentProps: {
                        addToPlaylist,
                        playlistId: playlist.id,
                        relay,
                        parent: this.props.playlist,
                        component: RecommendationContainer
                      },
                      title: 'Browse Recommendations',
                      hideActions: true
                    }
                  ]}
                  trigger={
                    <Thumbnail
                      playlist={playlist}
                      action={editable ? removePlaylistRecommendation : null}
                      actionClassName={[
                        styles.deleteRecommendation,
                        isMobile ? null : styles.showOnHover
                      ].join(' ')}
                      actionIcon={circleCloseIcon}
                    />
                  }
                  onClose={() => this.setState({ viewPlaylist: null })}
                />
              )}
              {editable &&
                playlistRecommendations.length === 0 &&
                <div className={styles.emptyList}>
                  <p>
                    You have not recommended any music to your clients.<br />
                    Give them some inspiration by{' '}
                    <a
                      onClick={() =>
                        this.setState({ addRecommendationsOpen: true })}
                    >
                      recommending a playlist
                    </a>.
                  </p>
                </div>}
            </div>
            {editable &&
              playlistRecommendations.length > 0 &&
              <a
                onClick={() => this.setState({ addRecommendationsOpen: true })}
              >
                Edit Recommendations
              </a>}
            {AddRecommendationsWizard}
          </div>}
      </RootConsumer>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Playlist/Recommendation/Recommendations.js